import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faOctagonCheck } from '@fortawesome/pro-regular-svg-icons';
import NavBarHome from '../Layouts/NavBarHome';
import FooterHome from '../Layouts/FooterHome';
import PageHeader from '../pagecomponents/PageHeader';

import analysisImg from '../pagecomponents/screenshots/ai/analysis.png';
import personalizationImg from '../pagecomponents/screenshots/ai/personalization.png';
import alertsImg from '../pagecomponents/screenshots/ai/alerts.png';

const styles = {
  container: {
    fontFamily: "'Helvetica Neue', Arial, sans-serif",
    margin: 0,
    padding: 0,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
  },
  main: {
    flex: 1,
    padding: '40px 20px',
    backgroundColor: '#f9f9f9',
  },
  introContainer: {
    maxWidth: '900px',
    margin: '0 auto',
    textAlign: 'center',
  },
  coreHeader: {
    textAlign: 'center',
    margin: '60px 0 40px',
  },
  coreTitle: {
    fontSize: '60px',
    margin: 0,
    fontWeight: '700',
    lineHeight: '1.1',
  },
  coreRed: {
    color: '#ff2300',
  },
  introText: {
    fontSize: '18px',
    color: '#333',
    lineHeight: '1.7',
    marginBottom: '20px',
    fontWeight: '500',
    whiteSpace: 'pre-wrap',
  },
  contentContainer: {
    width: '100%',
    backgroundColor: '#ff2300',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
    boxSizing: 'border-box',
    minHeight: '700px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  tabContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: '12px',
    marginBottom: '24px',
    flexWrap: 'wrap',
  },
  tabButton: {
    padding: '10px 20px',
    border: '2px solid #fff',
    borderRadius: '20px',
    backgroundColor: '#ff2300',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 500,
  },
  activeTabButton: {
    padding: '10px 20px',
    border: 'none',
    borderRadius: '20px',
    backgroundColor: '#fff',
    color: '#ff2300',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 600,
  },
  contentLayout: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap',
  },
  imageContainer: {
    flex: '1 1 60%',
    textAlign: 'center',
  },
  image: {
    width: '100%',
    maxWidth: '800px',
    height: 'auto',
    borderRadius: '8px',
  },
  textContainer: {
    flex: '1 1 35%',
    textAlign: 'left',
    color: '#fff',
  },
  sectionTitle: {
    fontSize: '24px',
    marginBottom: '16px',
    fontWeight: 600,
  },
  bulletList: {
    listStyle: 'none',
    paddingLeft: 0,
    fontSize: '18px',
    fontWeight: 400,
  },
  bulletItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '12px',
  },
};

export default function AiAssistantPage() {
  const [language, setLanguage] = useState(
    () => localStorage.getItem('language') || 'es'
  );
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const onResize = (e) => setIsMobile(e.matches);
    onResize(mq);
    mq.addEventListener('change', onResize);
    return () => mq.removeEventListener('change', onResize);
  }, []);

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const content = {
    es: {
      pageHeader: {
        title: 'IA = Asistente del Manager: Objetividad y alertas',
        subtitle:
          'Convierte la toma de decisiones de talento en un proceso senzillo. Pon a tu lado un Líder de Talento dedicado que te guía con datos objetivos y alertas inteligentes; despídete de la subjetividad, la inconsistencia y el tiempo perdido.',
        ctaText: 'Solicitar Demo',
      },
      intro:
        'Con nuestra inteligencia artificial ética, diseñada como Asistente del Manager, conviertes los datos de talento en acción: la plataforma analiza en tiempo real, con objetividad y profundidad, el desempeño y el potencial de cada persona, los cruza con una amplia base de contenidos de desarrollo y entrega recomendaciones relevantes, personalizadas y plenamente accionables; además, muestra alertas de oportunidad o riesgo y propone rutas de crecimiento alineadas con las metas del negocio. No sustituye el criterio ni la creatividad humana: los empodera con evidencia transparente y responsable, refuerza la consistencia en la gestión y acelera el progreso de cada equipo',
      sections: [
        {
          key: 'analysis',
          title: 'Datos Objetivos',
          bullets: [
            'Visión 360°: capaz de analizar datos de desempeño, potencial y riesgo en segundos.',
            'Sin sesgos humanos: algoritmos transparentes garantizan evaluación imparcial.',
            'Comparativas multiples: identifica fortalezas y áreas críticas al instante.',
          ],
        },
        {
          key: 'personalization',
          title: 'Ultra Personalización',
          bullets: [
            'Sugiere planes y acciones adaptadas al perfil y objetivos',
            'Itinerarios formativos individualizados según habilidades y áreas de mejora.',
            'Contenidos relevantes que fomentan compromiso y aprendizaje continuo.',
          ],
        },
        {
          key: 'alerts',
          title: 'Alertas',
          bullets: [
            'Olvídate de filtrar miles de datos, la IA resalta lo que requiere tu atención.',
            'Recibe alertas cuando indicadores clave cruzan umbrales críticos.',
            'Concentrarte en decisiones clave sin perderte en la analítica.',
          ],
        },
      ],
    },
    en: {
      pageHeader: {
        title: 'AI = Manager’s Assistant: Objectivity & Alerts',
        subtitle:
          'Transform talent decision-making into a seamless process. Keep a dedicated Talent Leader by your side who guides you with objective data and intelligent alerts; say goodbye to subjectivity, inconsistency, and wasted time.',
        ctaText: 'Request Demo',
      },
      intro:
        'With our ethical artificial intelligence designed as a Manager Assistant, you turn talent data into action: the platform analyzes real-time performance and potential objectively and in depth, cross-references it with an extensive development-content base, and delivers relevant, personalized, fully actionable recommendations. It also surfaces opportunity or risk alerts and proposes growth paths aligned with business goals. It doesn’t replace human judgment or creativity — it empowers them with transparent, responsible insights, reinforces consistency in management, and accelerates team progress.',
      sections: [
        {
          key: 'analysis',
          title: 'Objective Data',
          bullets: [
            '360° view: analyzes performance, potential, and risk data in seconds.',
            'Bias-free: transparent algorithms ensure impartial evaluation.',
            'Multi-level comparisons: instantly spot strengths and critical areas.',
          ],
        },
        {
          key: 'personalization',
          title: 'Ultra Personalization',
          bullets: [
            'Suggests plans and actions tailored to each profile and objective.',
            'Individual learning paths based on skills and areas for improvement.',
            'Relevant content that fuels engagement and continuous learning.',
          ],
        },
        {
          key: 'alerts',
          title: 'Alerts',
          bullets: [
            'Forget sifting through thousands of data points — AI highlights what needs your attention.',
            'Receive alerts whenever key indicators cross critical thresholds.',
            'Focus on strategic decisions without drowning in analytics.',
          ],
        },
      ],
    },
  };

  const lang = content[language];
  const [activeTab, setActiveTab] = useState(lang.sections[0].key);
  const current = lang.sections.find((s) => s.key === activeTab);
  const imageMap = {
    analysis: analysisImg,
    personalization: personalizationImg,
    alerts: alertsImg,
  };
  const handleCta = () => console.log('Demo solicitado');

  // responsive title size
  const coreTitleStyle = {
    ...styles.coreTitle,
    fontSize: isMobile ? '32px' : styles.coreTitle.fontSize,
  };

  return (
    <div style={styles.container}>
      <NavBarHome language={language} />

      <PageHeader
        title={lang.pageHeader.title}
        subtitle={lang.pageHeader.subtitle}
        ctaText={lang.pageHeader.ctaText}
        onCtaClick={handleCta}
      />

      <main style={styles.main}>
        {/* CLAIM: Inteligencia Artificial Ética */}
        <div style={styles.introContainer}>
          <hr
            style={{
              border: 'none',
              height: '2px',
              backgroundColor: '#ff2300',
              margin: '60px 0',
            }}
          />
          <div style={styles.coreHeader}>
            <h2 style={coreTitleStyle}>
              <span style={{ color: '#222', marginRight: '8px' }}>
                {language === 'es'
                  ? 'Inteligencia Artificial'
                  : 'Artificial Intelligence'}
              </span>
              <span style={styles.coreRed}>
                {language === 'es' ? 'Ética' : 'Ethics'}
              </span>
            </h2>
          </div>
          <p style={styles.introText}>{lang.intro}</p>
          <hr
            style={{
              border: 'none',
              height: '2px',
              backgroundColor: '#ff2300',
              margin: '60px 0',
              marginBottom: '150px',
            }}
          />
        </div>

        {/* Tabs & contenido */}
        <div style={styles.contentContainer}>
          <div style={styles.tabContainer}>
            {lang.sections.map((section) => (
              <button
                key={section.key}
                style={
                  section.key === activeTab
                    ? styles.activeTabButton
                    : styles.tabButton
                }
                onClick={() => setActiveTab(section.key)}
              >
                {section.title}
              </button>
            ))}
          </div>

          <div
            style={{
              ...styles.contentLayout,
              flexDirection: isMobile ? 'column' : 'row',
            }}
          >
            <div
              style={{
                ...styles.imageContainer,
                marginBottom: isMobile ? '20px' : 0,
              }}
            >
              <img
                src={imageMap[current.key]}
                alt={current.title}
                style={styles.image}
              />
            </div>

            <div style={styles.textContainer}>
              <h3 style={styles.sectionTitle}>{current.title}</h3>
              <ul style={styles.bulletList}>
                {current.bullets.map((item, i) => (
                  <li key={i} style={styles.bulletItem}>
                    <FontAwesomeIcon icon={faOctagonCheck} />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </main>

      <FooterHome language={language} setLanguage={setLanguage} />
    </div>
  );
}
